import { useState } from "react";
import { toast } from "sonner";
import { ShieldOff } from "lucide-react";
import { api, errMsg, useApi } from "../lib/api";
import { END_REASON, RESULT, STATUS, fmtDate } from "../lib/labels";
import { PageHeader } from "../components/Layout";
import { ConfirmDialog, Countdown, EmptyRow, PauseNote, StatusBadge } from "../components/Common";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const Filter = ({ value, onChange, placeholder, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-48 border-slate-700 bg-slate-900" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{placeholder}</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testid}-option-${o.key || o.value}`}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

const WarRow = ({ w, onCancel }) => {
  const active = w.status === "negotiation" || w.status === "fight";
  return (
    <tr className="row-hover border-b border-slate-800/70 hover:bg-slate-900/60" data-testid={`war-row-${w.id}`}>
      <td className="px-4 py-3 font-mono text-xs text-slate-400">{fmtDate(w.declared_at)}</td>
      <td className="px-4 py-3 font-medium">{w.zone?.name || "—"}</td>
      <td className="px-4 py-3 text-sm"><span className="text-rose-300">{w.attacker?.label || "—"}</span><span className="px-2 font-mono text-xs text-slate-600">vs</span><span className="text-cyan-300">{w.defender?.label || "Zona livre"}</span></td>
      <td className="px-4 py-3"><StatusBadge status={w.status} testid={`war-status-${w.id}`} />
        {active && <div className="mt-1 space-y-1"><Countdown war={w} className="font-mono text-xs" /><div><PauseNote war={w} testid={`war-row-pause-${w.id}`} /></div></div>}</td>
      <td className="px-4 py-3 font-mono text-sm"><span className="text-rose-300">{w.attacker_kills}</span> : <span className="text-cyan-300">{w.defender_kills}</span></td>
      <td className="px-4 py-3 text-sm" data-testid={`war-result-${w.id}`}>
        {w.result ? <span className={w.result === "draw" ? "text-slate-300" : "text-emerald-300"}>{RESULT[w.result]}</span> : <span className="text-slate-600">—</span>}
        {w.end_reason && <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{END_REASON[w.end_reason] || w.end_reason}</div>}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-500">{w.negotiation_minutes} + {w.fight_minutes} min</td>
      <td className="px-4 py-3 text-right">
        {active && <Button size="sm" variant="ghost" onClick={() => onCancel(w)} className="text-rose-400 hover:text-rose-300" data-testid={`war-cancel-button-${w.id}`}><ShieldOff className="mr-1 h-3.5 w-3.5" />Cancelar</Button>}
      </td>
    </tr>
  );
};

export default function WarsPage() {
  const [status, setStatus] = useState("all");
  const [zone, setZone] = useState("all");
  const [org, setOrg] = useState("all");
  const [cancelling, setCancelling] = useState(null);
  const params = new URLSearchParams();
  if (status !== "all") params.set("status", status);
  if (zone !== "all") params.set("zone_id", zone);
  if (org !== "all") params.set("org_id", org);
  const { data: wars, reload } = useApi(`/admin/wars?${params.toString()}`, 15000);
  const { data: zones } = useApi("/admin/zones");
  const { data: orgs } = useApi("/admin/orgs");

  const confirmCancel = async () => {
    try {
      await api.post(`/admin/wars/${cancelling.id}/force-cancel`);
      toast.success("Guerra cancelada.");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setCancelling(null);
  };

  return (
    <div>
      <PageHeader eyebrow="Registo de conflitos" title="Guerras" />
      <div className="fade-up mb-4 flex flex-wrap gap-3" data-testid="wars-filters">
        <Filter value={status} onChange={setStatus} placeholder="Todos os estados" testid="wars-filter-status"
          options={Object.entries(STATUS).map(([k, v]) => ({ value: k, label: v.label }))} />
        <Filter value={zone} onChange={setZone} placeholder="Todas as zonas" testid="wars-filter-zone"
          options={(zones || []).map((z) => ({ value: z.id, label: z.name }))} />
        <Filter value={org} onChange={setOrg} placeholder="Todas as organizações" testid="wars-filter-org"
          options={(orgs || []).map((o) => ({ value: o.id, key: o.name, label: o.label }))} />
      </div>
      <div className="panel fade-up overflow-x-auto">
        <table className="w-full text-left" data-testid="wars-table">
          <thead className="border-b border-slate-800 font-mono text-[11px] uppercase tracking-widest text-slate-500">
            <tr><th className="px-4 py-3">Declarada</th><th className="px-4 py-3">Zona</th><th className="px-4 py-3">Atacante vs Defensor</th><th className="px-4 py-3">Estado</th><th className="px-4 py-3">Kills</th><th className="px-4 py-3">Resultado</th><th className="px-4 py-3">Tempos</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {wars && wars.length === 0 && <EmptyRow cols={8} text="Nenhuma guerra encontrada." testid="wars-empty" />}
            {(wars || []).map((w) => <WarRow key={w.id} w={w} onCancel={setCancelling} />)}
          </tbody>
        </table>
      </div>
      <ConfirmDialog open={!!cancelling} onOpenChange={(o) => !o && setCancelling(null)} testid="war-cancel-dialog"
        title="Cancelar guerra?" confirmLabel="Cancelar guerra" onConfirm={confirmCancel}
        description={`A guerra em ${cancelling?.zone?.name || ""} será cancelada sem atribuição de pontos.`} />
    </div>
  );
}
